import {
  Flex,
  IconButton,
  IconCollectionLine,
  IconCollectionSolid,
  IconEditLine,
  Text,
  ToggleGroup,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { CourseGroupService } from "../../services/CourseGroupService";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import CourseGroupModal from "../Programs/CourseGroupModal";
import Courses from "./Courses";

const CourseGroup = ({ courseGroup, editPermission }) => {
  const showNotification = useNotification();
  const [cgData, setCgData] = useState(courseGroup);
  const [loading, setLoading] = useState(false);
  const [openCgModal, setOpenCgModal] = useState(false);
  const [refetchCourses, setRefetchCourses] = useState(false);
  const [courseGroupExpanded, setCourseGroupExpanded] = useState(false);

  useEffect(() => {
    if (refetchCourses) {
      CourseGroupService.getCoursesByGroupById(courseGroup.id)
        .then((coursesObj) => {
          setCgData((prevData) => ({
            ...prevData,
            courses: coursesObj.courses,
          }));
          setRefetchCourses(false);
          setCourseGroupExpanded(true);
        })
        .catch(() => {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("programs.courses"),
            }),
            ERROR
          );
        });
    }
  }, [courseGroup.id, refetchCourses, showNotification]);

  const handleOnToggle = (expanded) => {
    setCourseGroupExpanded(expanded);
    if (!cgData.courses) {
      setLoading(true);
      CourseGroupService.getCoursesByGroupById(courseGroup.id)
        .then((coursesObj) => {
          setCgData((prevData) => ({
            ...prevData,
            courses: coursesObj.courses,
          }));
          setLoading(false);
        })
        .catch(() => {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("programs.courses"),
            }),
            ERROR
          );
        });
    }
  };

  return (
    <>
      <ToggleGroup
        size="small"
        border={false}
        toggleLabel={t("academicHierarchy.toggleCourseGroup")}
        iconExpanded={IconCollectionSolid}
        icon={IconCollectionLine}
        expanded={courseGroupExpanded}
        onToggle={(_e, expanded) => handleOnToggle(expanded)}
        summary={
          <Flex gap="small">
            <Flex.Item shouldShrink>
              <Text>{cgData.name}</Text>
            </Flex.Item>
            {editPermission && (
              <Flex.Item shouldGrow textAlign="end">
                <Tooltip
                  color="primary-inverse"
                  renderTip={t("academicHierarchy.editCourseGroup")}
                >
                  <IconButton
                    id="edit-course-group"
                    renderIcon={IconEditLine}
                    screenReaderLabel={t("academicHierarchy.editCourseGroup")}
                    onClick={() => {
                      handleOnToggle();
                      setOpenCgModal(true);
                    }}
                  />
                </Tooltip>
              </Flex.Item>
            )}
          </Flex>
        }
      >
        <div style={{ paddingLeft: 40 }}>
          {!loading ? (
            cgData.courses ? (
              <Courses courses={cgData.courses} />
            ) : (
              <div style={{ marginLeft: 25, padding: 10 }}>
                {t("academicHierarchy.noCourses")}
              </div>
            )
          ) : (
            <Skeleton style={{ height: 64, borderRadius: 5 }} />
          )}
        </div>
      </ToggleGroup>
      <CourseGroupModal
        open={openCgModal}
        setOpen={setOpenCgModal}
        data={cgData}
        setData={setCgData}
        setTrigger={setRefetchCourses}
        title={t("programs.editCourseGroup")}
      />
    </>
  );
};

export default CourseGroup;
